/**
 * Contacts — the mailable audience.
 *
 * Identity and the order figures are refreshed hourly from the customer and
 * order tables; this screen edits only what a marketer owns (status, merge
 * fields). That is why the aggregates are shown read-only.
 */
define('mailingSubscribersView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var PAGE_SIZE = 50;

  var SubscribersView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-subscribers',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-toolbar">' +
      '  <input type="search" class="mlg-search" placeholder="Search name or email…">' +
      '  <span class="mlg-count"></span>' +
      '</div>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading contacts…</div></div>' +
      '<div class="mlg-pager"></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      pager: '.mlg-pager',
      count: '.mlg-count',
      search: '.mlg-search',
      modal: '.mlg-modal'
    },

    events: {
      'keyup .mlg-search': 'onSearch',
      'search .mlg-search': 'onSearch',
      'click .mlg-page': 'onPage',
      'click .mlg-new-subscriber': 'onNew',
      'click .mlg-edit': 'onEdit',
      'click .mlg-history': 'onHistory',
      'click .mlg-delete': 'onDelete',
      'click .mlg-sync': 'onSync',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onSave'
    },

    initialize: function () {
      this.page = 1;
      this.search = '';
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('subscribers',
          '<button type="button" class="mlg-btn mlg-sync">Refresh from orders</button>' +
          '<button type="button" class="mlg-btn primary mlg-new-subscriber">New contact</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      MailingApi.subscribers({ page: this.page, limit: PAGE_SIZE, search: this.search })
        .done(function (result) {
          self.renderList(result);
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' +
            _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    renderList: function (result) {
      var rows = result.data || [];
      this.ui.count.text(Shell.formatNumber(result.total) + ' contact(s)');

      if (!rows.length) {
        this.ui.body.html(Shell.emptyHtml(this.search ?
            'No contact matches this search.' :
            'No contact yet. "Refresh from orders" imports everyone who has ordered.'));
        this.ui.pager.empty();
        return;
      }

      var body = _.map(rows, function (subscriber) {
        var name = ((subscriber.firstName || '') + ' ' + (subscriber.lastName || '')).trim();
        return '<tr data-secid="' + subscriber.secId + '">' +
          '<td class="c-name">' +
          '  <span class="n">' + _.escape(name || '—') + '</span>' +
             (subscriber.vip ? '<span class="mlg-chip vip">VIP</span>' : '') +
          '  <div class="c-sub">' + _.escape(subscriber.email || '') + '</div>' +
          '</td>' +
          '<td><span class="mlg-chip st-' + (subscriber.status || 'ACTIVE').toLowerCase() + '">' +
              Shell.prettyCase(subscriber.status) + '</span></td>' +
          '<td>' + _.escape(subscriber.webshopLocale || subscriber.language || '—') + '</td>' +
          '<td class="num">' + Shell.formatNumber(subscriber.numberOfOrders) + '</td>' +
          '<td class="num">' + Shell.formatMoney(subscriber.totalTurnover) + '</td>' +
          '<td>' + Shell.formatDate(subscriber.lastOrder) + '</td>' +
          '<td>' + Shell.formatDate(subscriber.lastMailedAt, true) + '</td>' +
          '<td class="c-actions">' +
          '  <button type="button" class="mlg-btn small mlg-history">History</button>' +
          '  <button type="button" class="mlg-btn small mlg-edit">Edit</button>' +
          '  <button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' +
          '</td>' +
          '</tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr>' +
        '<th>Contact</th><th>Status</th><th>Locale</th><th class="num">Orders</th>' +
        '<th class="num">Turnover</th><th>Last order</th><th>Last mailed</th><th></th>' +
        '</tr></thead><tbody>' + body + '</tbody></table>');

      this.renderPager(result);
    },

    renderPager: function (result) {
      var pages = result.pages || 1;
      if (pages <= 1) {
        this.ui.pager.empty();
        return;
      }
      var current = result.page || 1;
      var first = Math.max(1, current - 3);
      var last = Math.min(pages, first + 6);
      var buttons = '';
      if (current > 1) {
        buttons += '<button type="button" class="mlg-page" data-page="' + (current - 1) + '">‹</button>';
      }
      for (var page = first; page <= last; page++) {
        buttons += '<button type="button" class="mlg-page' + (page === current ? ' active' : '') +
          '" data-page="' + page + '">' + page + '</button>';
      }
      if (current < pages) {
        buttons += '<button type="button" class="mlg-page" data-page="' + (current + 1) + '">›</button>';
      }
      this.ui.pager.html(buttons);
    },

    onPage: function (e) {
      this.page = Number($(e.currentTarget).data('page')) || 1;
      this.load();
    },

    /** Debounced so typing doesn't fire a request per keystroke. */
    onSearch: function () {
      var self = this;
      var value = this.ui.search.val();
      if (this.searchTimer) {
        window.clearTimeout(this.searchTimer);
      }
      this.searchTimer = window.setTimeout(function () {
        self.search = value;
        self.page = 1;
        self.load();
      }, 300);
    },

    //----------Edit---------------------------------------------------------//

    onNew: function () {
      this.openEditor({});
    },

    onEdit: function (e) {
      var self = this;
      MailingApi.subscriber(this.secIdOf(e)).done(function (subscriber) {
        self.openEditor(subscriber);
      });
    },

    openEditor: function (subscriber) {
      function field(label, cls, value) {
        return '<label>' + label + '<input type="text" class="' + cls + '" value="' +
          _.escape(value || '') + '"></label>';
      }
      var statuses = ['ACTIVE', 'UNSUBSCRIBED', 'BOUNCED'];
      var statusOptions = _.map(statuses, function (status) {
        return '<option value="' + status + '"' +
          (status === (subscriber.status || 'ACTIVE') ? ' selected' : '') + '>' +
          Shell.prettyCase(status) + '</option>';
      }).join('');

      this.editingSecId = subscriber.secId || null;
      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>' + (subscriber.secId ? 'Edit contact' : 'New contact') + '</h3>' +
        '<div class="mlg-row">' +
          field('Email', 'f-email', subscriber.email) +
          '<label>Status<select class="f-status">' + statusOptions + '</select></label>' +
        '</div>' +
        '<div class="mlg-row">' +
          field('First name', 'f-first-name', subscriber.firstName) +
          field('Last name', 'f-last-name', subscriber.lastName) +
        '</div>' +
        '<div class="mlg-row">' +
          field('Country code', 'f-country-code', subscriber.countryCode) +
          field('Language', 'f-language', subscriber.language) +
        '</div>' +
        '<div class="mlg-row">' +
          field('Webshop', 'f-webshop', subscriber.webshop) +
          field('Webshop locale', 'f-webshop-locale', subscriber.webshopLocale) +
        '</div>' +
        '<div class="mlg-row">' +
          field('Webshop phone', 'f-webshop-phone', subscriber.webshopLocalePhone) +
          field('Offer picture URL', 'f-offer-picture', subscriber.offerEmailSeqPicture1) +
        '</div>' +
        '<div class="mlg-row">' +
          field('Offer URL', 'f-offer-url', subscriber.offerEmailSeqUrl1) +
          field('Offer POI', 'f-offer-poi', subscriber.offerEmailSeqPoi1) +
        '</div>' +
        '<div class="mlg-row">' +
          field('Offer price', 'f-offer-price', subscriber.offerEmailSeqPrice1) +
          field('Offer quantity', 'f-offer-quantity', subscriber.offerEmailSeqQuantity1) +
        '</div>' +
        '<div class="mlg-hint">Order figures (turnover, order count, VIP) come from the customer record ' +
          'and are refreshed automatically — they cannot be edited here.</div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Save</button>' +
        '</div>' +
        '</div>').show();
    },

    onSave: function () {
      var self = this;
      var $modal = this.ui.modal;
      var data = {
        email: $modal.find('.f-email').val(),
        status: $modal.find('.f-status').val(),
        firstName: $modal.find('.f-first-name').val(),
        lastName: $modal.find('.f-last-name').val(),
        countryCode: $modal.find('.f-country-code').val(),
        language: $modal.find('.f-language').val(),
        webshop: $modal.find('.f-webshop').val(),
        webshopLocale: $modal.find('.f-webshop-locale').val(),
        webshopLocalePhone: $modal.find('.f-webshop-phone').val(),
        offerEmailSeqPicture1: $modal.find('.f-offer-picture').val(),
        offerEmailSeqUrl1: $modal.find('.f-offer-url').val(),
        offerEmailSeqPoi1: $modal.find('.f-offer-poi').val(),
        offerEmailSeqPrice1: $modal.find('.f-offer-price').val(),
        offerEmailSeqQuantity1: $modal.find('.f-offer-quantity').val()
      };
      if (!data.email || data.email.indexOf('@') === -1) {
        Shell.banner(this.$el, 'A valid email is required', 'error');
        return;
      }
      var saving = this.editingSecId ?
          MailingApi.updateSubscriber(this.editingSecId, data) :
          MailingApi.createSubscriber(data);

      saving.done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Contact saved', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    closeModal: function () {
      this.editingSecId = null;
      this.ui.modal.hide().empty();
    },

    //----------History------------------------------------------------------//

    onHistory: function (e) {
      var self = this;
      var secId = this.secIdOf(e);
      var email = $(e.currentTarget).closest('tr').find('.c-sub').text();

      MailingApi.subscriberHistory(secId).done(function (history) {
        var rows = !history.length ?
            '<tr><td colspan="6" class="mlg-empty">No email sent to this contact yet.</td></tr>' :
            _.map(history, function (entry) {
            return '<tr>' +
              '<td>' + Shell.formatDate(entry.sentAt, true) + '</td>' +
              '<td>' + _.escape(entry.sourceName || '—') + '</td>' +
              '<td>' + _.escape(entry.subject || '') + '</td>' +
              '<td><span class="mlg-chip st-' + (entry.status || '').toLowerCase() + '">' +
                  Shell.prettyCase(entry.status) + '</span></td>' +
              '<td class="num">' + Shell.formatNumber(entry.openCount) + '</td>' +
              '<td class="num">' + Shell.formatNumber(entry.clickCount) + '</td>' +
              '</tr>';
          }).join('');

        self.ui.modal.html(
          '<div class="mlg-modal-card wide">' +
          '<h3>Mailing history — ' + _.escape(email) + '</h3>' +
          '<table class="mlg-table compact">' +
          '<thead><tr><th>Sent</th><th>Source</th><th>Subject</th><th>Status</th>' +
          '<th class="num">Opens</th><th class="num">Clicks</th></tr></thead>' +
          '<tbody>' + rows + '</tbody></table>' +
          '<div class="mlg-modal-actions">' +
          '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
          '</div>' +
          '</div>').show();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDelete: function (e) {
      var self = this;
      var email = $(e.currentTarget).closest('tr').find('.c-sub').text();
      if (!window.confirm('Delete ' + email + '? Their send history goes with them.')) { return; }

      MailingApi.deleteSubscriber(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Contact deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    /** Runs the order aggregation on demand instead of waiting for the hourly job. */
    onSync: function () {
      var self = this;
      Shell.banner(this.$el, 'Refreshing from orders — this can take a moment…', 'ok');
      MailingApi.syncSubscribers().done(function (result) {
        Shell.banner(self.$el, (result.refreshed || 0) + ' contact(s) refreshed', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = SubscribersView;
});

